import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_LOGO = process.env.REACT_APP_DEFAULT_LOGO_URL;

// Helper function to get full image URL
const getImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  return `${BACKEND_URL}${path}`;
};

const AdminLogin = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  React.useEffect(() => {
    // Fetch settings for logo
    axios.get(`${API}/settings`).then(res => setSettings(res.data)).catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      localStorage.setItem('adminToken', response.data.access_token);
      toast.success('Succesvol ingelogd!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ongeldige inloggegevens');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="admin-login-card">
        <CardHeader className="text-center">
          {(getImageUrl(settings?.logo_url) || DEFAULT_LOGO) ? (
            <img 
              src={getImageUrl(settings?.logo_url) || DEFAULT_LOGO} 
              alt="Feestcomité Leupegem" 
              className="h-24 w-auto mx-auto mb-4"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
          ) : null}
          <div style={{display: (getImageUrl(settings?.logo_url) || DEFAULT_LOGO) ? 'none' : 'block'}} className="text-3xl font-bold mb-4 text-[#0a56c2]">
            Feestcomité Leupegem
          </div>
          <CardTitle className="text-2xl" data-testid="login-title">Admin Login</CardTitle>
          <CardDescription>Beheer de website content</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gebruikersnaam</label>
              <Input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                required
                data-testid="username-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Wachtwoord</label>
              <Input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                required
                data-testid="password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-[#0a56c2] hover:bg-[#083d8a]"
              disabled={isLoading}
              data-testid="login-submit-button"
            >
              {isLoading ? 'Inloggen...' : 'Inloggen'}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <a href="/" className="text-sm text-[#0a56c2] hover:underline" data-testid="back-to-home-link">
              Terug naar website
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;